export type HighlightKind = 'new' | 'improved' | 'fix';

export type ReleaseNoteHighlight = {
  title: string;
  description: string;
  href?: string;
  kind?: HighlightKind;
};

export type ReleaseNote = {
  id: string;
  version: string;
  label?: string;
  date: string;
  title: string;
  summary: string;
  highlights: ReleaseNoteHighlight[];
};

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: '2026-07-11',
    version: '1.1.0',
    label: 'Summer update',
    date: '2026-07-11',
    title: 'Inbox, CRM & AI — all in one release',
    summary:
      'A redesigned multi-channel inbox, CRM exports and deals/payments, AI reply suggestions for Instagram and Facebook, and post publish notifications.',
    highlights: [
      {
        kind: 'improved',
        title: 'Unified channels inbox',
        description:
          'WhatsApp, Instagram, and Facebook share one cleaner inbox — assign agents, set priority, filter by tags or campaigns, and pick templates from a side drawer.',
        href: '/home/inbox',
      },
      {
        kind: 'new',
        title: 'Leads & customers export',
        description:
          'Download Excel or CSV exports in the background — you get a notification when the file is ready.',
        href: '/home/leads',
      },
      {
        kind: 'new',
        title: 'Deals, plans & payment links',
        description:
          'Manage deal payments, CRM plans, and Razorpay subscription links — including offers and payment progress.',
        href: '/home/deals',
      },
      {
        kind: 'new',
        title: 'AI suggestions for IG & Facebook',
        description:
          'Get reply suggestions in Instagram and Facebook chats so your team can respond faster.',
        href: '/home/instagram/inbox',
      },
      {
        kind: 'new',
        title: 'Post publish notifications',
        description:
          'Get notified when a scheduled Instagram or Facebook post publishes — or if it fails.',
        href: '/home/instagram/posts',
      },
    ],
  },
];

export const WHATS_NEW_SEEN_PREF_KEY = 'whats_new_seen_id';
export const WHATS_NEW_SEEN_KEY = 'wenext_whats_new_seen_id';
export const WHATS_NEW_SEEN_EVENT = 'wenext:whats-new-seen';

const SEMVER_RE = /^\d+\.\d+\.\d+$/;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): StorageLike {
  if (typeof localStorage === 'undefined') {
    return { getItem: () => null, setItem: () => undefined };
  }
  return localStorage;
}

export function whatsNewSeenStorageKey(userId?: string | null): string {
  const id =
    userId != null && String(userId).trim() ? String(userId).trim() : 'anonymous';
  return `${WHATS_NEW_SEEN_KEY}:${id}`;
}

export function getLatestRelease(): ReleaseNote | null {
  return RELEASE_NOTES[0] ?? null;
}

export function getCurrentAppVersion(): string {
  return getLatestRelease()?.version ?? '0.0.0';
}

export function formatVersion(version: string): string {
  const trimmed = (version || '').trim();
  if (!trimmed) return 'v0.0.0';
  return trimmed.startsWith('v') || trimmed.startsWith('V')
    ? `v${trimmed.slice(1)}`
    : `v${trimmed}`;
}

export function isValidSemVer(version: string): boolean {
  return SEMVER_RE.test((version || '').trim());
}

export function highlightKindLabel(kind?: HighlightKind): string {
  switch (kind) {
    case 'improved':
      return 'Improved';
    case 'fix':
      return 'Fix';
    case 'new':
    default:
      return 'New';
  }
}

function emitSeenChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(WHATS_NEW_SEEN_EVENT));
  } catch {
    // ignore
  }
}

export function getWhatsNewSeenIdFromPreferences(
  preferences: unknown,
): string | null {
  if (!preferences) return null;
  let prefs = preferences;
  if (typeof prefs === 'string') {
    try {
      prefs = JSON.parse(prefs);
    } catch {
      return null;
    }
  }
  if (!prefs || typeof prefs !== 'object' || Array.isArray(prefs)) return null;
  const raw = (prefs as Record<string, unknown>)[WHATS_NEW_SEEN_PREF_KEY];
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed || null;
}

export function syncWhatsNewSeenFromPreferences(
  userId: string | null | undefined,
  preferences: unknown,
  storage: StorageLike = defaultStorage(),
): string | null {
  const fromServer = getWhatsNewSeenIdFromPreferences(preferences);
  if (!fromServer || !userId) return fromServer;
  try {
    const key = whatsNewSeenStorageKey(userId);
    const local = storage.getItem(key);
    if (local !== fromServer) {
      storage.setItem(key, fromServer);
      emitSeenChanged();
    }
  } catch {
    // ignore
  }
  return fromServer;
}

export function getSeenReleaseId(
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): string | null {
  try {
    const key = whatsNewSeenStorageKey(userId);
    let raw = storage.getItem(key);
    if (!raw || !raw.trim()) {
      const legacy = storage.getItem(WHATS_NEW_SEEN_KEY);
      if (legacy && legacy.trim()) {
        raw = legacy.trim();
        try {
          storage.setItem(key, raw);
        } catch {
          // ignore
        }
      }
    }
    return raw && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

export function markReleaseSeen(
  id: string,
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): void {
  if (!id) return;
  try {
    storage.setItem(whatsNewSeenStorageKey(userId), id);
    emitSeenChanged();
  } catch {
    // ignore
  }
}

export async function markReleaseSeenPersistent(
  id: string,
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): Promise<boolean> {
  if (!id) return false;
  markReleaseSeen(id, userId, storage);

  try {
    const { markWhatsNewSeenOnServer } = await import('@/lib/api/services/whatsNew');
    const result = await markWhatsNewSeenOnServer(id);
    if (result.ok) {
      const seenId = result.whats_new_seen_id ?? id;
      markReleaseSeen(seenId, userId, storage);
      try {
        const raw = storage.getItem('user');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            const prefs =
              parsed.preferences && typeof parsed.preferences === 'object'
                ? { ...parsed.preferences }
                : {};
            prefs[WHATS_NEW_SEEN_PREF_KEY] = seenId;
            parsed.preferences = prefs;
            storage.setItem('user', JSON.stringify(parsed));
          }
        }
      } catch {
        // ignore
      }
    }
    return result.ok;
  } catch {
    return false;
  }
}

export function markLatestReleaseSeen(
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): void {
  const latest = getLatestRelease();
  if (!latest) return;
  markReleaseSeen(latest.id, userId, storage);
}

export async function markLatestReleaseSeenPersistent(
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): Promise<boolean> {
  const latest = getLatestRelease();
  if (!latest) return false;
  return markReleaseSeenPersistent(latest.id, userId, storage);
}

export function shouldShowWhatsNewModal(
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
  preferences?: unknown,
): boolean {
  const latest = getLatestRelease();
  if (!latest) return false;
  const fromPrefs = getWhatsNewSeenIdFromPreferences(preferences);
  if (fromPrefs) {
    if (userId) syncWhatsNewSeenFromPreferences(userId, preferences, storage);
    return fromPrefs !== latest.id;
  }
  return getSeenReleaseId(userId, storage) !== latest.id;
}

export function formatReleaseDate(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
